import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, ToggleButton, ToggleButtonGroup } from '@mui/material';
import SearchResultGrid from '../searchResultGrid/searchResultGrid';
import Case from '../../interfaces/case';
import { caseColumnDef, caseColumnGroup } from '../../colDef/caseColumnDef';
import ToolbarContainer from '../baseComponents/toolbarContainer';
import Filter from '../../interfaces/filters';
import { currentPoolMonth, debounce, decodedJWT, getOwnerType, handleSearchCase, isGridLeafColumn } from '../../utils/commonUtils';
import FilterDrawer from './filterDrawer';
import RoleManagement from '../../security/roleManagement';
import JWTTokenPayload from '../../interfaces/JWTTokenPayload';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { createFilters, getSelectedFiltersCount } from '../../utils/filterUtils';
import SeeDetailsDrawer from '../modals/seeDetailsDrawer';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import CaseCarousel from './caseCarouselComponent';

const agencyFilters = ['interaction_type', 'pin_code', 'dpd_bucket', 'client_name', 'loan_type', 'status', 'l0_interaction', 'segment_id', 'state', 'district', 'focused'];
const clientFilters = ['interaction_type', 'client_manager_username', 'pin_code', 'dpd_bucket', 'agency_name', 'loan_type', 'status', 'l0_interaction', 'state', 'district'];

const FocusedCases: React.FC = () => {
	const token: JWTTokenPayload | false = decodedJWT();
	const ownerType = getOwnerType(token);

	const { gridSelectedCase } = useSelector((state: RootState) => state.gridSelectedCase);
	const segments = useSelector((state: RootState) => state.segments.segments);
	const clients = useSelector((state: RootState) => state.clients.clients);
	const agencies = useSelector((state: RootState) => state.agencies.agencies);
	const picklist = useSelector((state: RootState) => state.picklists.picklists);
	const districtAndStates = useSelector((state: RootState) => state.districtsAndStates);
	const states = districtAndStates.states;
	const districts = districtAndStates.districts.map(val => val.district);
	const userSlice = useSelector((state: RootState) => state.userList);
	const users = userSlice.users;
	const clientManagerList = userSlice.clientManagerDescendants;

	const [filterResults, setFilterResults] = useState<Case[]>([]);
	const [leftMarginDashboard, setLeftMarginDashboard] = useState<string>('16px');
	const [loading, setLoading] = useState<boolean>(false);
	const [paginationModel, setPaginationModel] = useState<{ page: number, pageSize: number }>({ page: 0, pageSize: 40 });
	const [rowCount, setRowCount] = useState<number>(0);
	const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
	const [filters, setFilters] = useState<Filter[]>([]);
	const [universalSearchQuery, setUniversalSearchQuery] = useState<string>("");
	const [selectedPoolMonth, setSelectedPoolMonth] = useState<string>(currentPoolMonth()); // Store month in parent
	const [groupState, setGroupState] = React.useState<Record<string, { visible: boolean; button: boolean; }>>({
		'Case Data': { visible: true, button: false },
		'Latest Interaction': { visible: false, button: true },
		'Best Interaction': { visible: false, button: true },
		'Latest Call Interaction': { visible: false, button: true },
		'Best Call Interaction': { visible: false, button: true },
		'Latest Visit Interaction': { visible: false, button: true },
		'Best Visit Interaction': { visible: false, button: true },
		'Pincode Data': { visible: true, button: false },
		'Timestamps': { visible: true, button: false },
	});
	const [viewMode, setViewMode] = useState<'grid' | 'carousel'>('grid');

	const visibleColumns = React.useMemo(() => {
		const result = [];
		for (const groupKey of caseColumnGroup) {
			const cols = groupKey.children.map(val => {
				if (isGridLeafColumn(val)) {
					return val.field;
				}
				return "";
			});
			result.push(...(groupState[groupKey.groupId].visible ? cols : [cols[0]]));
		}
		const visibleColumnsSet: Set<string> = new Set(result);
		return caseColumnDef.filter((col) => visibleColumnsSet.has(col.field));
	}, [groupState]);

	const toggleGroup = (groupKey: string) => {
		setGroupState((prev) => ({
			...prev,
			[groupKey]: { visible: !prev[groupKey].visible, button: prev[groupKey].button },
		}));
	};

	const clearResults = () => {
		setFilterResults([]);
		setRowCount(0);
	};

	const setDefaultFilters = () => {
		let finalFilters = createFilters(
			RoleManagement.isAgency(ownerType) ? agencyFilters : clientFilters, segments.filter(val => val.is_assigned_to_user), clients, agencies, picklist, states, districts, users, clientManagerList
		);
		// find the filter with key === 'focused' and set its value
		const idx = finalFilters.findIndex(f => f.key === 'focused');
		if (idx !== -1) {
			const focused = { ...finalFilters[idx] };
			focused.value = focused.multiple ? ['true'] : 'true';
			focused.disabled = true;
			// replace in the array immutably
			finalFilters = [
				...finalFilters.slice(0, idx),
				focused,
				...finalFilters.slice(idx + 1)
			];
		}
		setFilters(finalFilters);
	};

	useEffect(() => {
		setDefaultFilters();
	}, [segments, clients]);

	const debouncedSearch = useMemo(() => debounce(handleSearchCase, 500), [paginationModel, selectedPoolMonth, universalSearchQuery, filters]);

	useEffect(() => {
		debouncedSearch.cancel(); // for safety don't in case the reference to the last function is lost and is not created again then this will be useful
		debouncedSearch(filters, universalSearchQuery, paginationModel.page, selectedPoolMonth, false, segments, "/api/v1/agent_case_allocation/combine_allocation", setLoading, clearResults, setRowCount, setFilterResults);
		return () => {
			debouncedSearch.cancel();
		};
	}, [paginationModel, universalSearchQuery, filters, selectedPoolMonth]);

	const toggleDrawer = () => {
		setDrawerOpen(!drawerOpen);
		if (!drawerOpen) {
			setLeftMarginDashboard('166px');
		} else {
			setLeftMarginDashboard('16px');
		}
	};

	const resetFilters = () => {
		setDefaultFilters();
		setPaginationModel({ page: 0, pageSize: 40 });
	};


	return (
		<>
			{loading ? (
				<Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<CircularProgress color="primary" />
				</Box>
			) : viewMode === 'grid' ? (
				// GRID VIEW: header + grid + details drawer
				<Box sx={{ ml: leftMarginDashboard, mr: 2, fontSize: '12.5px' }}>
					<FilterDrawer
						filters={filters}
						setFilters={setFilters}
						drawerOpen={drawerOpen}
						toggleDrawer={toggleDrawer}
					/>
					<ToolbarContainer
						resetFilters={resetFilters}
						setFilters={setFilters}
						toggleDrawer={toggleDrawer}
						filtersCount={getSelectedFiltersCount(filters)}
						universalSearchQuery={universalSearchQuery}
						setUniversalSearchQuery={setUniversalSearchQuery}
						selectedPoolMonth={selectedPoolMonth}
						setSelectedPoolMonth={setSelectedPoolMonth}
					/>
					<Box sx={{ display: 'flex', justifyContent: 'end', marginBottom: 1 }}>
						<ToggleButtonGroup
							value={viewMode}
							exclusive
							onChange={(_, val) => {
								if (!val) return;
								setViewMode(val);
							}}
							size="small"
							aria-label="view mode"
						>
							<ToggleButton value="grid" aria-label="grid view">
								<GridViewIcon />
							</ToggleButton>
							<ToggleButton value="carousel" aria-label="carousel view">
								<ViewCarouselIcon />
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>

					<SearchResultGrid
						height="70vh"
						disableFilter={true}
						disableSorting={true}
						disableAsecDescSorting={true}
						getRowId={(row) => row.case_id}
						results={filterResults}
						onSelectRow={() => { }}
						columns={visibleColumns}
						rowCount={rowCount}
						paginationModel={paginationModel}
						setPaginationModel={setPaginationModel}
						columnGroupModel={caseColumnGroup}
						showToolBar={false}
						groupVisibilityMap={groupState}
						toggleGroupVisibility={toggleGroup}
					/>

					<SeeDetailsDrawer />
				</Box>
			) : (
				// CAROUSEL VIEW
				<Box sx={{ height: '70vh', mt: 1 }}>
					<CaseCarousel viewMode={viewMode} setViewMode={setViewMode} />
				</Box>
			)}
		</>
	);
};

export default FocusedCases;